import React from 'react';
import { Text, StyleSheet, View, Linking, Platform } from 'react-native';
import { translate } from 'secullum-i18n';
import {
  Card,
  Button,
  Space,
  Link,
  RadioButton,
  Message
} from 'secullum-react-native-ui';
import { getTheme } from '../../shared/modules/layout';
import Api from '../modules/api';
import { carregarDadosLoginAsync } from '../modules/auth';
import { DadosFuncionario, DadosEmpresa } from '../modules/types';
import ModalAjuda from './ModalSolicitarAjuda';
import SolicitarAjudaForm from './SolicitarAjudaForm';

export interface CardSolicitarAjudaProps {
  bancoSelecionado?: string;
  usuarioLogado?: boolean;
  onNavegar?: () => void;
  onErroConexao?: (mensagem: string, onTentarNovamente: () => void) => void;
}

interface State {
  dadosFuncionario?: DadosFuncionario;
  dadosEmpresa: DadosEmpresa;
  opcaoSelecionada: number;
  exibirAjuda: boolean;
  bancoId: string;
  erroGeral: string;
}

class CardSolicitarAjuda extends React.Component<
  CardSolicitarAjudaProps,
  State
> {
  state: State = {
    dadosEmpresa: {
      nome: '',
      telefone: '',
      email: '',
      caminhoTermoPrivacidade: ''
    },
    opcaoSelecionada: 0,
    exibirAjuda: false,
    bancoId: '',
    erroGeral: ''
  };

  async componentDidMount() {
    await this.carregarDadosEmpresa();
  }

  carregarDadosEmpresa = async () => {
    const bancoSelecionado = await this.buscarBancoSelecionado();

    if (bancoSelecionado.length == 0) {
      return;
    }

    const api = new Api();

    await api.getDadosEmpresaPrincipal(bancoSelecionado, {
      onSuccess: dados => {
        this.setState({
          bancoId: bancoSelecionado,
          dadosFuncionario: api.funcionario.state.dados,
          dadosEmpresa: dados
        });
      },
      onError: err => {
        if (err.name === 'TimeoutError' && this.props.onErroConexao) {
          this.props.onErroConexao(err.message, () =>
            this.carregarDadosEmpresa()
          );
        } else {
          this.setState({ erroGeral: err.message });
        }
      }
    });
  };

  buscarBancoSelecionado = async () => {
    if (this.props.bancoSelecionado) {
      return this.props.bancoSelecionado;
    }

    const dadosLogin = await carregarDadosLoginAsync();

    return dadosLogin ? dadosLogin.bancoId : '';
  };

  handleButtonPress = async (opcaoSelecionada: number) => {
    const { onNavegar } = this.props;

    if (opcaoSelecionada === 1) {
      this.setState({ exibirAjuda: true });
      return;
    }
    onNavegar && onNavegar();
  };

  render() {
    const { dadosEmpresa, exibirAjuda, dadosFuncionario, bancoId, erroGeral } =
      this.state;

    return (
      <>
        <Card nativeID="card-solicitar-ajuda">
          {!exibirAjuda
            ? this.renderInformacoesEmpresa(bancoId, dadosEmpresa)
            : this.renderFormAjuda(bancoId, dadosFuncionario, dadosEmpresa)}
        </Card>
        <Message
          nativeID="solicitar-ajuda-mensagem-erro-geral"
          type="warning"
          visible={erroGeral.length > 0}
          message={erroGeral}
          onRequestClose={() => this.setState({ erroGeral: '' })}
        />
      </>
    );
  }

  renderInformacoesEmpresa = (bancoId: string, dadosEmpresa: DadosEmpresa) => {
    const items = [
      {
        value: 0,
        label: translate(
          'Vou entrar em contato com a empresa onde trabalho por conta própria'
        ),
        nativeID: 'solicitar-ajuda-opcao-empresa'
      },
      {
        value: 1,
        label: translate(
          'A empresa onde trabalho não consegue resolver o problema que estou tendo'
        ),
        nativeID: 'solicitar-ajuda-opcao-secullum'
      }
    ];

    const { opcaoSelecionada } = this.state;

    return (
      <Card.Section style={Platform.OS === 'web' && styles.section}>
        <Text style={styles.text}>
          {translate(
            'Este aplicativo trabalha com dados fornecidos pela empresa onde você trabalha.'
          )}
        </Text>
        <Space height={6} />
        <Text style={styles.text}>
          {translate(
            'Pedimos que caso tenha alguma dúvida ou problema, consulte a empresa primeiramente para que eles possam lhe ajudar nisso.'
          )}
        </Text>
        <Space />
        <View>
          {bancoId.length == 0 ? (
            <Text style={styles.text}>
              {translate(
                'Infelizmente não foi possível identificar o contato da sua empresa.'
              )}
            </Text>
          ) : (
            <View>
              <Text style={styles.text}>
                {translate('Os dados para contato são:')}
              </Text>
              <Space height={6} />
              <Text style={styles.text}>{dadosEmpresa.nome.toUpperCase()}</Text>
              {dadosEmpresa.telefone !== null &&
                dadosEmpresa.telefone.length > 0 && (
                  <>
                    <Space height={5} />
                    <Link
                      text={dadosEmpresa.telefone}
                      onPress={() => {
                        Linking.openURL('tel:' + dadosEmpresa.telefone);
                      }}
                      style={styles.link}
                    />
                  </>
                )}
              {dadosEmpresa.email && dadosEmpresa.email.length > 0 && (
                <>
                  <Space height={5} />
                  <Link
                    text={dadosEmpresa.email}
                    onPress={() => {
                      Linking.openURL('mailto:' + dadosEmpresa.email);
                    }}
                    style={styles.link}
                  />
                </>
              )}
            </View>
          )}
        </View>
        <Space />
        <RadioButton
          items={items}
          value={opcaoSelecionada}
          onChange={value =>
            this.setState({
              opcaoSelecionada: value
            })
          }
        />
        <Space height={16} />
        <Button
          text={
            opcaoSelecionada ? translate('Solicitar Ajuda à Secullum') : 'OK'
          }
          onPress={() => this.handleButtonPress(opcaoSelecionada)}
          nativeID="botao-ok-solicitar-ajuda"
        />
      </Card.Section>
    );
  };

  renderFormAjuda = (
    bancoId: string,
    dadosFuncionario?: DadosFuncionario,
    dadosEmpresa?: DadosEmpresa
  ) => {
    return Platform.OS === 'web' ? (
      <Card.Section style={Platform.OS === 'web' && styles.sectionForm}>
        {!this.props.usuarioLogado && (
          <View style={styles.cardHeader}>
            <Text style={styles.title}>{translate('Solicitar Ajuda')}</Text>
          </View>
        )}
        <SolicitarAjudaForm
          dadosFuncionario={dadosFuncionario}
          empresaBancoId={bancoId}
          nomeEmpresa={dadosEmpresa?.nome || ''}
          caminhoTermoPrivacidade={dadosEmpresa?.caminhoTermoPrivacidade || ''}
          onNavegar={this.props.onNavegar}
          onClose={() => this.setState({ exibirAjuda: false })}
          onErroConexao={this.props.onErroConexao}
        />
      </Card.Section>
    ) : (
      <ModalAjuda
        dadosFuncionario={dadosFuncionario}
        empresaBancoId={bancoId}
        nomeEmpresa={dadosEmpresa?.nome || ''}
        caminhoTermoPrivacidade={dadosEmpresa?.caminhoTermoPrivacidade || ''}
        onNavegar={this.props.onNavegar}
        onClose={() => this.setState({ exibirAjuda: false })}
        onErroConexao={this.props.onErroConexao}
      />
    );
  };
}

const theme = getTheme();

const styles = StyleSheet.create({
  text: {
    justifyContent: 'flex-start',
    fontSize: 16,
    color: theme.textColor3
  },
  link: {
    justifyContent: 'flex-start',
    fontSize: 16,
    textDecorationLine: 'underline'
  },
  section: {
    margin: 'auto',
    maxWidth: 350
  },
  sectionForm: {
    margin: 'auto',
    width: '100%'
  },
  cardHeader: {
    paddingBottom: 10,
    flexDirection: 'row'
  },
  title: {
    flexDirection: 'row',
    fontSize: 22,
    fontWeight: 'bold',
    color: theme.textColor3,
    alignSelf: 'flex-start'
  }
});
export default CardSolicitarAjuda;
